/* client/src/App.tsx */
import React, { useState, useEffect, useCallback } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Transactions from './components/Transactions';
import Expenses from './components/Expenses';
import Reports from './components/Reports';
import Investments from './components/Investments';
import Login from './components/Login';
import Layout from './components/Layout/Layout';
import './App.css';
import Inventory from './components/Inventory';
import AdminPanel from './components/Admin/AdminPanel';
import { MaterialsProvider } from './contexts/MaterialsContext';
import ProfitManagement from './components/ProfitManagement';
import { apiVerifyToken, apiLogout } from './services/apiService'; 

// Modern theme similar to Orbai
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2', // MNC blue instead of purple
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#10B981', // Modern green
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1E293B',
      secondary: '#64748B',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

// Cookie helper function
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [lastVerifyTime, setLastVerifyTime] = useState(0);

  // Single useEffect for authentication verification
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 2;

    const verifyAuth = async () => {
      const now = Date.now();
      if (now - lastVerifyTime < 2000) {
        if (isMounted) {
          setLoading(false);
          setAuthChecked(true);
        }
        return;
      }
      setLastVerifyTime(now);

      try {
        // Check if we have a token before making the request
        const token = localStorage.getItem('token') || getCookie('token');
        if (!token) {
          console.log('No token found, skipping verification');
          if (isMounted) {
            setLoading(false);
            setAuthChecked(true);
            setIsAuthenticated(false);
          }
          return;
        }

        // Use the correct API service function
        const response = await apiVerifyToken();
        if (isMounted) {
          if (response.user) {
            setUser(response.user);
            setIsAuthenticated(true);
            localStorage.setItem('user', JSON.stringify(response.user));
          } else {
            // Clear invalid token
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } catch (error: any) {
        if (isMounted) {
          console.error('Auth verification failed:', error);
          
          // Handle rate limiting specifically
          if (error.message.includes('Too many verification requests') || error.message.includes('Too many requests')) {
            if (retryCount < maxRetries) {
              retryCount++;
              console.log(`Rate limited, retrying in 2 seconds (${retryCount}/${maxRetries})`);
              setTimeout(verifyAuth, 2000);
              return;
            }
          }
          
          // Clear invalid tokens on other errors
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          setIsAuthenticated(false);
        }
      } finally {
        if (isMounted && retryCount === 0) {
          setLoading(false);
          setAuthChecked(true);
        }
      }
    };

    verifyAuth();

    return () => {
      isMounted = false;
    };
  }, [lastVerifyTime]);

  const handleLogin = useCallback((userData: any) => {
  // The Login component should return both user and token in the userData object
  if (userData.token) {
    localStorage.setItem('token', userData.token); // Store token as backup
  }
  localStorage.setItem('user', JSON.stringify(userData.user || userData));
  setUser(userData.user || userData);
  setIsAuthenticated(true);
  setAuthChecked(true);
}, []);

  const handleLogout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
      setAuthChecked(true);
    }
  }, []);

  // Show loading spinner only during initial auth check
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <Router>
          {isAuthenticated && user ? (
            <MaterialsProvider>
              <Layout user={user} onLogout={handleLogout}>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/transactions" element={<Transactions />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/investments" element={<Investments />} />
                  <Route path="/inventory" element={<Inventory />} />
                  <Route path="/profit-management" element={<ProfitManagement />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/admin" element={<AdminPanel />} /> 
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Layout>
            </MaterialsProvider>
          ) : authChecked ? (
            <Login onLogin={handleLogin} />
          ) : null}
        </Router>
      </div>
    </ThemeProvider>
  );
}

export default App;